import { Injectable } from '@angular/core';

import { generateUuid } from '@core/utils-legacy';

import type { Redactor, RedactorConfig } from '../redactor.types';
import { mergeRedactorConfigArrField, RedactorExtension } from './redactor-extension';

declare let $R: Redactor;

// plugin constants
export const ANGULAR_ADAPTER_PLUGIN_TOKEN = 'rt-adapter';
export const ANGULAR_ADAPTER_INLINE_COMPONENT_TAG = 'redactor-inline-component';

export interface RedactorComponentAdapterSettings {
  data: any;
  angularTagName: string;
}

@Injectable()
export class RedactorAdapterExtension extends RedactorExtension {
  // eslint-disable-next-line max-lines-per-function
  constructor() {
    super();

    if (!$R.classes[ANGULAR_ADAPTER_PLUGIN_TOKEN]) {
      $R.add('plugin', ANGULAR_ADAPTER_PLUGIN_TOKEN, {
        _trackedIds: null,
        _hostsToBeCreated: null,
        _hostsInitialData: null,
        _afterBufferAction: false,
        init(app: any): void {
          this.app = app;
          this.cleaner = app.cleaner;
          this.opts = app.opts;
          this.component = app.component;
          this.utils = app.utils;
          this.insertion = app.insertion;
          this.inspector = app.inspector;
          this.caret = app.caret;
          this.selection = app.selection;
          this.keycodes = app.keycodes;
        },
        onobserve(): void {
          // console.time('angular-adapter.observe');

          const toRemoveIds = Array.from(this._trackedIds);
          const toRemove = toRemoveIds.filter((id: string) => !document.getElementById(id));

          const toAddIds = Array.from(this._hostsToBeCreated);
          const toAdd = toAddIds.filter((id: string) => document.getElementById(id));

          if (toAdd.length || toRemove.length) {
            this.app.broadcast(
              'ngComponentsDiff',
              toAdd,
              toRemove,
              [],
              new Map(this._hostsInitialData),
            );

            toAdd.forEach(id => this._trackedIds.add(id));
            toRemove.forEach(id => this._trackedIds.delete(id));

            this._hostsToBeCreated.clear();
          }

          // console.timeEnd('angular-adapter.observe');
        },
        onundo(html: string): void {
          this._onBufferAction(html);
        },
        onredo(html: string): void {
          this._onBufferAction(html);
        },
        start(): void {
          this._hostsInitialData = new Map<string, { [key: string]: any }>();
          this._hostsToBeCreated = new Set<string>();
          this._trackedIds = new Set<string>();
        },
        stop(): void {
          this.app.broadcast('ngComponentsDiff', [], Array.from(this._trackedIds), []);
          this._hostsToBeCreated.clear();
          this._hostsInitialData.clear();
          this._trackedIds.clear();
        },
        registerHost(id: string, initialData: { [key: string]: any }): void {
          this._hostsInitialData.set(id, initialData);
          this._hostsToBeCreated.add(id);
        },
        updateHostData(id: string, data: { [key: string]: any }): void {
          this._hostsInitialData.set(id, data);
          this.app.broadcast('ngDataChange', id, data);
        },
        getHostData(id: string): { [key: string]: any } {
          return this._hostsInitialData.get(id);
        },
        onkeydown(e: KeyboardEvent): void {
          if (this.selection.isAll()) {
            return;
          }

          const caretPos = [this.keycodes.LEFT, this.keycodes.UP].includes(e.keyCode)
            ? 'setBefore'
            : 'setAfter';

          const node = this._getActiveComponentOnSelection();

          if (node) {
            this.caret[caretPos](node);
          }
        },
        _getActiveComponentOnSelection(): HTMLElement | false {
          const current = this.selection.getCurrent();
          const data = this.inspector.parse(current);
          return data.isComponentType(ANGULAR_ADAPTER_PLUGIN_TOKEN) ? data.getComponent() : false;
        },
        _onBufferAction(html: string): void {
          // console.time('angular-adapter._onBufferAction');

          // @ts-ignore
          const current = Array.from(html.matchAll(/id="(.{36})"/gm)).map(([_, id]) => id);
          const existing = current.filter((id: string) => document.getElementById(id));
          const removed = current.filter(id => !existing.includes(id));
          this._trackedIds = new Set(existing);
          this.app.broadcast(
            'ngComponentsDiff',
            [],
            removed,
            existing,
            new Map(this._hostsInitialData),
          );

          // console.timeEnd('angular-adapter._onBufferAction');
        },
      });

      $R.add('class', `${ANGULAR_ADAPTER_PLUGIN_TOKEN}.component`, {
        mixins: ['dom', 'component'],
        _host: null,
        _data: {},
        init: function (
          app: Redactor,
          elementOrConfig: HTMLElement | RedactorComponentAdapterSettings,
        ): void {
          const $el = elementOrConfig instanceof HTMLElement ? <HTMLElement>elementOrConfig : null;
          const config = $el ? null : <RedactorComponentAdapterSettings>elementOrConfig;

          this._data = config ? config.data : null;

          this.app = app;
          this.opts = app.opts;
          this.utils = app.utils;
          this.source = app.source;

          this._createComponent($el, config ? config.angularTagName : null);

          return this;
        },
        setAngularComponentState(data: { [key: string]: any }): void {
          this._data = { ...this._ngHostData, ...data };
          this.app.api('plugin.rt-adapter.updateHostData', this.getId(), this.getData());
          this.app.broadcast('ngContentChange');
        },
        writeValue(value: string): void {
          this.data('interpolated-value', encodeURI(value));
        },
        getId(): string {
          return this._host.get().id;
        },
        getData(): any {
          return this._data;
        },
        pushDataToAngularComponent(data: { [key: string]: any }, interp: string): void {
          this._data = { ...this._ngHostData, ...data };
          this.writeValue(interp);
          this.app.api('plugin.rt-adapter.updateHostData', this.getId(), this.getData());
          this.app.broadcast('ngContentChange');
        },
        _createComponent($el: any, angularTagName: string): void {
          if ($el) {
            this.parse($el);
            this._host = this.find('.angular-adapter-component');
          } else {
            this._buildComponentFromScratch(angularTagName);
          }
        },
        _buildComponentFromScratch(angularTagName: string): void {
          this.parse(
            `<${ANGULAR_ADAPTER_INLINE_COMPONENT_TAG} class="redactor-component angular-adapter"><${angularTagName} contenteditable="false" class="angular-adapter-component"></${angularTagName}></${ANGULAR_ADAPTER_INLINE_COMPONENT_TAG}>`,
          );

          this._host = this.find(angularTagName);
          this._host.get().id = generateUuid();

          this.app.api('plugin.rt-adapter.registerHost', this.getId(), this.getData());

          this.data({
            'redactor-span': true,
            'redactor-type': ANGULAR_ADAPTER_PLUGIN_TOKEN,
            'angular-type': angularTagName,
          });

          this._appendInvisibleChar();

          this.attr({
            contenteditable: false,
            tabindex: '-1',
          });
        },
        _appendInvisibleChar(): void {
          const { utils: redactorUtils } = this.app;
          const hostParentHTMLElement = this._host.get().parentElement;
          const invisibleCharNode = <HTMLElement>redactorUtils.createInvisibleChar();
          hostParentHTMLElement.append(invisibleCharNode);
        },
      });
    }
  }

  public canInstall(config: RedactorConfig): boolean {
    return true;
  }

  public install(config: RedactorConfig): RedactorConfig {
    return {
      ...config,
      plugins: mergeRedactorConfigArrField(config, 'plugins', [ANGULAR_ADAPTER_PLUGIN_TOKEN]),
      inlineTags: mergeRedactorConfigArrField(config, 'inlineTags', [
        ANGULAR_ADAPTER_INLINE_COMPONENT_TAG,
      ]),
      removeComments: false,
    };
  }
}
